import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import PropTypes from 'prop-types';

import { useTransl } from 'shared/lib/i18n';

import EEHistoryPlaybackComponent from './EEHistoryPlaybackComponent';

import { getCameraOnTimeIntervals } from './actions';

import {
  formatEESeconds,
  formatEETimestamp,
  generateCameraOnTimeIntervals,
  generateTimelineMarks,
  getDayStartEndTimestamp,
  getEEVideoListQuery,
  getIsBeforeNow,
  getMinMaxMarksValue,
  getStartEndTimestamp,
  getStartOfYesterday,
  getTimelineDurationOptions,
  getTimelineNextTime,
  getTimelinePrevTime,
  inCurrentTimezone,
  inUTCTimezone,
  parseCameraOnTime,
  parseEESeconds,
} from './utils';

// all datetime values are in seconds

const EEHistoryPlaybackContainer = (props) => {
  const { t } = useTransl();
  const { building, camera, sessionId } = props;

  // datetime picker state
  const [dateTimePickerValue, setDateTimePickerValue] = useState(
    getStartOfYesterday(),
  );
  // ee start time
  const [startTime, setStartTime] = useState(null);
  // current timeline time
  const [timeline, setTimeline] = useState(Number(dateTimePickerValue));
  // ee end time
  const [endTime, setEndTime] = useState(null);
  // duration picker
  const [timelineDuration, setTimelineDuration] = useState(
    getTimelineDurationOptions()[4],
  );

  const timelineHours = timelineDuration?.value;

  const [timelineMarks, setTimelineMarks] = useState(
    generateTimelineMarks(timeline, timelineHours),
  );

  const { minTimelineValue, maxTimelineValue } =
    getMinMaxMarksValue(timelineMarks);

  // data from ee with camera on time
  const [cameraOnTime, setCameraOnTime] = useState(null);

  // video state
  const [isVideoStreamOn, setIsVideoStreamOn] = useState(false);
  const [isPlayOn, setIsPlayOn] = useState(false);

  const [videoConfig, setVideoConfig] = useState(null);
  const [downloadData, setDownloadData] = useState({});

  // requests camera 'on' and 'off' time for the selected time period on the timeline
  const onGetCameraOnTimeIntervals = async (start, end) => {
    try {
      const config = {
        esn: camera.liveViewId,
        auth_key: sessionId,
        start,
        end,
      };
      const res = await getCameraOnTimeIntervals(getEEVideoListQuery(config));
      Array.isArray(res?.data) && setCameraOnTime(parseCameraOnTime(res?.data));
    } catch (err) {
      toast.warn(t('camera.cannotLoadData'));
    }
  };

  const getCurrentClip = (timestamp) =>
    cameraOnTime?.find(
      ({ startTime, endTime }) => startTime <= timestamp && timestamp < endTime,
    );

  const onDateRangeChange = (timestamp, newDuration) => {
    const newTimelineValue = timestamp ?? dateTimePickerValue;
    const timelineDuration = newDuration ?? timelineHours;

    setTimeline(newTimelineValue);

    const newTimelineMarks = generateTimelineMarks(
      newTimelineValue,
      timelineDuration,
    );
    setTimelineMarks(newTimelineMarks);

    const { startTime, endTime } = getStartEndTimestamp(
      newTimelineValue,
      timelineDuration,
    );
    onGetCameraOnTimeIntervals(startTime, endTime);
  };

  const onDateTimeChange = (date) => {
    const utcDate = inUTCTimezone(formatEESeconds(date));
    setDateTimePickerValue(utcDate);
    onDateRangeChange(utcDate);
  };

  const getVideoElement = () =>
    document.getElementById(`camera-${camera?.liveViewId}`);

  const onPauseVideo = () => {
    const videoEl = getVideoElement();
    if (!videoEl) return;
    setIsPlayOn(false);
    videoEl.pause();
  };

  const onResumeVideo = () => {
    const videoEl = getVideoElement();
    if (!videoEl) return;
    setIsPlayOn(true);
    videoEl.play();
  };

  const onPlayVideo = () => {
    if (isVideoStreamOn && !isPlayOn) {
      onResumeVideo();
      return;
    }
    setIsVideoStreamOn(true);
    setIsPlayOn(true);
  };

  const updateVideoTiming = (timestamp) => {
    const currentClip = getCurrentClip(timestamp);

    if (currentClip) {
      setStartTime(timestamp);
      setTimeline(timestamp);
      setEndTime(currentClip?.endTime);
      return;
    }

    const nextClip = cameraOnTime?.find(
      ({ startTime }) => startTime >= timestamp,
    );

    if (nextClip && nextClip?.startTime < maxTimelineValue) {
      setStartTime(nextClip?.startTime);
      setTimeline(nextClip?.startTime);
      setEndTime(nextClip?.endTime);
      return;
    }

    if (nextClip && nextClip?.startTime >= maxTimelineValue) {
      onDateRangeChange(nextClip?.startTime);
      return;
    }

    if (!nextClip) {
      getIsBeforeNow(inCurrentTimezone(timestamp)) &&
      getIsBeforeNow(inCurrentTimezone(maxTimelineValue))
        ? onDateRangeChange(timestamp)
        : onPauseVideo();
    }
  };

  useEffect(() => {
    const { startTime, endTime } = getDayStartEndTimestamp(dateTimePickerValue);
    onGetCameraOnTimeIntervals(startTime, endTime);
  }, []);

  useEffect(() => {
    if (!cameraOnTime?.length) return;
    updateVideoTiming(timeline);
    generateCameraOnTimeIntervals(
      timelineMarks,
      cameraOnTime,
      minTimelineValue,
    );
  }, [cameraOnTime]);

  useEffect(() => {
    startTime && endTime && onPlayVideo();
  }, [startTime, endTime]);

  // updates current time indicator on the timeline
  // with taking into account camera 'on' and 'off' time
  const onTimeUpdateCallback = (currentPlaybackSecondsValue) => {
    const newTimelineValue =
      Number(startTime) + Number(currentPlaybackSecondsValue);

    if (newTimelineValue === timeline) return;
    if (newTimelineValue - timeline > 1) return;

    const currentClip = getCurrentClip(newTimelineValue);

    if (currentClip) {
      setTimeline(newTimelineValue);
      newTimelineValue >= maxTimelineValue &&
        onDateRangeChange(newTimelineValue);
      return;
    }

    updateVideoTiming(newTimelineValue);
  };

  // update indicator value on the timeline and start playback from this time
  const onSetTimeline = (value) => {
    updateVideoTiming(value);
  };

  const onVideoEnded = () => {
    updateVideoTiming(Number(timeline) + 1);
  };

  const onSetTimelineDuration = (event, newDuration) => {
    setTimelineDuration(newDuration);
    onDateRangeChange(timeline, newDuration?.value);
  };

  const onScrollTimelineBack = () =>
    onDateRangeChange(getTimelinePrevTime(minTimelineValue, timelineHours));

  const onScrollTimelineForth = () =>
    onDateRangeChange(getTimelineNextTime(minTimelineValue, timelineHours));

  const isNextTimeDisabled = !getIsBeforeNow(
    inCurrentTimezone(maxTimelineValue),
  );

  return (
    <EEHistoryPlaybackComponent
      building={building}
      camera={camera}
      timestamp={parseEESeconds(inCurrentTimezone(dateTimePickerValue))}
      onTimestampChange={onDateTimeChange}
      isStreamOn={isVideoStreamOn}
      isPlayOn={isPlayOn}
      onPlayVideo={onPlayVideo}
      onPauseVideo={onPauseVideo}
      startTime={formatEETimestamp(parseEESeconds(startTime))}
      endTime={formatEETimestamp(parseEESeconds(endTime))}
      timeline={Number(timeline)}
      setTimeline={onSetTimeline}
      timelineMarks={timelineMarks}
      onTimeUpdateCallback={onTimeUpdateCallback}
      onVideoEnded={onVideoEnded}
      setVideoConfig={setVideoConfig}
      videoConfig={videoConfig}
      onScrollTimelineBack={onScrollTimelineBack}
      onScrollTimelineForth={onScrollTimelineForth}
      timelineDuration={timelineDuration}
      setTimelineDuration={onSetTimelineDuration}
      downloadData={downloadData}
      setDownloadData={setDownloadData}
      isNextTimeDisabled={isNextTimeDisabled}
    />
  );
};

EEHistoryPlaybackContainer.defaultProps = {};

EEHistoryPlaybackContainer.propTypes = {
  building: PropTypes.instanceOf(Object).isRequired,
  camera: PropTypes.instanceOf(Object).isRequired,
  sessionId: PropTypes.string.isRequired,
};

export default EEHistoryPlaybackContainer;
